import { useCallback, useEffect, useMemo, useState } from "react";

import { Box, Button, Flex, Typography } from "@aircall/tractor";
import { useForm } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { EvaluationAccordionHeaderContent } from "../EvaluationAccordionHeaderContent";
import { ScoreCardLocationState } from "../EvaluationNew";

import { useCreateEvaluation } from "@/hooks/useCreateEvaluation/useCreateEvaluation";
import { useGenerateAIAnswers } from "@/hooks/useGenerateAIAnswers/useGenerateAIAnswers";
import { EvaluationCreateFormFooter } from "./components/EvaluationCreateFormFooter";
import {
  EvaluationCreateFormBinaryQuestion,
  EvaluationCreateFormRatingQuestion,
} from "./components/EvaluationFormQuestion";
import { EvaluationCreateFormProvider } from "./contexts/EvaluationCreateForm/EvaluationCreateFormContext";

import { Accordion } from "@/components/Accordion";
import { AccordionBody } from "@/components/Accordion/components/AccordionBody";
import { AccordionHeader } from "@/components/Accordion/components/AccordionHeader";
import { AccordionItem } from "@/components/Accordion/components/AccordionItem";
import { ConfirmationModal } from "@/components/ConfirmationModal";
import { useBoolean } from "@/hooks/useBoolean/useBoolean";

import { CloseOutlined } from "@aircall/icons";
import {
  AccordionItemId,
  CreateEvaluationInput,
  EvaluationCreateFormProps,
  initCategories,
  initRatingScales,
  initSet,
  mergeExistingFormCategoriesWithGeneratedAIAnswers,
} from ".";
import { useTracking } from "./EvaluationCreateForm.hooks";

export function EvaluationCreateForm({
  scoreCard,
  callId,
  revieweeUserId,
  transcriptionId,
}: EvaluationCreateFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [editedAIQuestions, setEditedAIQuestions] = useState(new Set());

  const { onClick: handleOnClickTrack } = useTracking();

  const {
    value: isConfirmationModalOpen,
    setFalse: closeConfirmationModal,
    setTrue: openConfirmationModal,
  } = useBoolean();

  const addEditedAIQuestion = useCallback((questionId: string) => {
    setEditedAIQuestions((prevSet) => {
      const newSet = new Set(prevSet);

      newSet.add(questionId);

      return newSet;
    });
  }, []);

  const { createEvaluation, loadingCreateEvaluation } = useCreateEvaluation();

  const {
    generatedAIAnswersMap: generatedQuestionsMap,
    loadingGeneratedAnswers,
  } = useGenerateAIAnswers(callId, scoreCard, transcriptionId);

  // Map answer values to custom points.
  const ratingScales = useMemo(() => initRatingScales(scoreCard), [scoreCard]);

  const form = useForm<CreateEvaluationInput>({
    onSubmit: async ({ value }) => {
      createEvaluation({ input: value });
    },
    defaultValues: {
      categories: initCategories(scoreCard.categories),
      feedback: "",
      // Below are hidden fields.
      callId,
      scoreCardId: scoreCard.ID,
      revieweeUserId,
      scoreCardName: scoreCard.name,
      scoreCardLanguage: scoreCard.language,
      scoreCardDescription: scoreCard.description,
    },
  });

  useEffect(() => {
    if (generatedQuestionsMap) {
      const currentCategories = form.getFieldValue("categories");

      const mergedCategories =
        mergeExistingFormCategoriesWithGeneratedAIAnswers(
          currentCategories,
          generatedQuestionsMap
        );

      form.setFieldValue("categories", mergedCategories);
    }
  }, [form, generatedQuestionsMap]);

  // Accordion state.
  const [openedItems, setOpenedItems] = useState<Set<AccordionItemId>>(
    () => new Set(initSet(form.state.values.categories))
  );

  const handleOnClickAccordionItem = useCallback((id: AccordionItemId) => {
    setOpenedItems((prevSet) => {
      const newSet = new Set(prevSet);

      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }

      return newSet;
    });
  }, []);

  const cancelEvaluationHandler = () => {
    handleOnClickTrack();
    navigate("../empty", {
      state: {
        scoreCardId: undefined,
      } as ScoreCardLocationState,
    });
  };

  const handleOnClickChevron = useCallback(
    (categoryId: AccordionItemId) => () => {
      handleOnClickAccordionItem(categoryId);
    },
    [handleOnClickAccordionItem]
  );

  const hasAIAnswer = (questionId: string, questionAiEnabled: boolean) =>
    Boolean(
      questionAiEnabled &&
        !editedAIQuestions.has(questionId) &&
        generatedQuestionsMap
    );

  return (
    <>
      <EvaluationCreateFormProvider form={form} ratingScales={ratingScales}>
        <Flex flexDirection="column" gap="xxs" data-test="evaluation-section">
          <Flex
            justifyContent="space-between"
            alignItems="center"
            p="xxs s"
            borderBottom="1px solid"
            borderColor="graphic-default"
          >
            <Typography variant="headingBoldS">{scoreCard.name}</Typography>
            <Button
              size="small"
              mode="ghost"
              variant="secondary"
              data-test="remove-scorecard-button"
              onClick={openConfirmationModal}
            >
              <CloseOutlined />
            </Button>
          </Flex>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();

              form.handleSubmit();
            }}
          >
            <Flex flexDirection="column" gap="m">
              <Box backgroundColor="surface-default">
                <Accordion openedItems={openedItems}>
                  {form.state.values.categories.map(
                    (category, categoryIndex) => (
                      <AccordionItem
                        key={category.categoryOrder}
                        id={category.categoryOrder}
                        borderBottom="1px solid"
                        borderColor="graphic-default"
                      >
                        <AccordionHeader
                          onClick={handleOnClickChevron(category.categoryOrder)}
                          cursor="pointer"
                        >
                          {/* We could put the remaining number of questions in the header */}
                          <EvaluationAccordionHeaderContent
                            title={category.categoryName}
                            open={openedItems.has(category.categoryOrder)}
                          />
                        </AccordionHeader>
                        <AccordionBody>
                          <Flex p="s" flexDirection="column" gap="xl">
                            {form.state.values.categories[
                              categoryIndex
                            ].questions.map((question, questionIndex) => {
                              if (question.questionPossibleRatingAnswers) {
                                return (
                                  <EvaluationCreateFormRatingQuestion
                                    key={`${category.categoryOrder}-${questionIndex}`}
                                    order={question.questionOrder + 1}
                                    text={question.questionText}
                                    possibleAnswers={
                                      question.questionPossibleRatingAnswers
                                    }
                                    categoryIndex={categoryIndex}
                                    questionIndex={questionIndex}
                                    isAIEnabled={question.questionAiEnabled}
                                    loadingAIQuestions={loadingGeneratedAnswers}
                                    hasAiAnswer={hasAIAnswer(
                                      question.scoreCardQuestionId,
                                      question.questionAiEnabled
                                    )}
                                    onEditQuestion={() =>
                                      addEditedAIQuestion(
                                        question.scoreCardQuestionId
                                      )
                                    }
                                  />
                                );
                              }

                              if (question.questionPossibleBinaryAnswers) {
                                return (
                                  <EvaluationCreateFormBinaryQuestion
                                    key={`${category.categoryOrder}-${questionIndex}`}
                                    order={question.questionOrder + 1}
                                    text={question.questionText}
                                    possibleAnswers={
                                      question.questionPossibleBinaryAnswers
                                    }
                                    categoryIndex={categoryIndex}
                                    questionIndex={questionIndex}
                                    isAIEnabled={question.questionAiEnabled}
                                    loadingAIQuestions={loadingGeneratedAnswers}
                                    hasAiAnswer={hasAIAnswer(
                                      question.scoreCardQuestionId,
                                      question.questionAiEnabled
                                    )}
                                    onEditQuestion={() =>
                                      addEditedAIQuestion(
                                        question.scoreCardQuestionId
                                      )
                                    }
                                  />
                                );
                              }

                              throw new Error("Unknown question type.");
                            })}
                          </Flex>
                        </AccordionBody>
                      </AccordionItem>
                    )
                  )}
                </Accordion>
              </Box>
              <EvaluationCreateFormFooter
                loading={loadingCreateEvaluation}
                onClickCancel={openConfirmationModal}
                scoreCard={scoreCard}
              />
            </Flex>
          </form>
        </Flex>
      </EvaluationCreateFormProvider>
      <ConfirmationModal
        onHide={closeConfirmationModal}
        show={isConfirmationModalOpen}
        actions={[
          {
            label: t("evaluation.keep_editing"),
            onClick: closeConfirmationModal,
          },
          {
            variant: "critical",
            label: t("evaluation.remove_it"),
            onClick: cancelEvaluationHandler,
          },
        ]}
        title={t("evaluation.cancel_evaluation")}
        description={t("evaluation.cancel_evaluation_description", {
          scoreCardName: scoreCard.name,
        })}
      />
    </>
  );
}
